import { AxiosPromise, AxiosResponse } from 'axios';
import { apiService, manageFilters } from '@app/services';
import { IActiveFilter } from '@app/models';
import { standardLicenseTypeName, premiumLicenseTypeName } from '@app/helpers';

class TenantsService {
    readonly apiBase: string = '/api/directory/Tenants';

    getTenants(
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        handleSuccess: Function,
        handleError: Function,
        filters?: IActiveFilter[]
    ): AxiosPromise {
        return apiService
            .get(`${this.apiBase}/detailed`, {
                params: {
                    $orderBy: orderBy + ' ' + asc,
                    $top: top,
                    $skip: skip,
                    $count: isReset,
                    ...(filters ? { $filter: manageFilters(filters) } : { $filter: manageFilters([]) })
                },
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getTenantByHomeRealm(homeRealm: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .get(`${this.apiBase}/detailed?$filter=HomeRealm eq '${homeRealm}'`, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getTenant(tenantId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .get(`${this.apiBase}(${tenantId})?$expand=Attributes`, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getTenantAuthPolicy(tenantId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .get(`${this.apiBase}(${tenantId})/AuthenticationPolicies`, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    findAor(tenantId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .get(`${this.apiBase}(${tenantId})/FindAor`, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getTenantLicense(tenantId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .get(`${this.apiBase}(${tenantId})/License`, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    getTenantOrgs(tenantId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        return apiService
            .get(`${this.apiBase}(${tenantId})/Organizations`, {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    deleteTenant(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/DeleteTenants`;

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    resetAorToken(tenantId: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${tenantId})/ResetAorToken`;

        return apiService.post(apiUrl, null, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    addTenant(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = '/api/registration';

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    updateSalesForceId(tenantId: string, salesForceId: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${tenantId})/SetSalesforceId`;

        let jsonObject: any = {};
        jsonObject.salesforceId = salesForceId.trim();

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    updateTenantType(tenantId: string, usingType: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${tenantId})/SetUsingType`;

        let jsonObject: any = {};

        jsonObject.usingType = usingType.trim();

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    updateAorUser(tenantId: string, username: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${tenantId})/SetAor`;

        let jsonObject: any = {};
        jsonObject.aor = username.trim();

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    rescueTenant(tenantId: string, email: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${tenantId})/Rescue`;

        let jsonObject: any = {};
        jsonObject.email = email.trim();

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    updateBetaOptIns(tenantId: string, optins: string[], handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${tenantId})/UpdateBetaOptins`;

        let jsonObject: any = {};
        jsonObject.optins = [];

        optins.map(function (value: string, index: number) {
            jsonObject.optins[index] = value;
        });

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    resetPolicy(tenantId: string, elementId: string, policyId: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${tenantId})/ToggleAuthenticationPolicy`;

        let jsonObject: any = {};
        jsonObject.elementId = elementId;
        jsonObject.policyId = policyId;

        return apiService.post(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    editLicense(
        tenantId: string,
        licenseState: string,
        expireDate: Date,
        orgLimit: number,
        standardSeats: number,
        premiumSeats: number,
        handleError: Function,
        handleSuccess: Function
    ): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${tenantId})/License`;

        let jsonObject: any = {};
        jsonObject.State = licenseState;
        jsonObject.Expiry = expireDate;
        jsonObject.OrganizationLimit = orgLimit;
        jsonObject.Entries = [];
        jsonObject.Entries[0] = {};
        jsonObject.Entries[0].EditionId = '00000000-0000-0000-0000-000000000001';
        jsonObject.Entries[0].Name = standardLicenseTypeName;
        jsonObject.Entries[0].Seats = standardSeats;
        jsonObject.Entries[1] = {};
        jsonObject.Entries[1].EditionId = '00000000-0000-0000-0000-000000000002';
        jsonObject.Entries[1].Name = premiumLicenseTypeName;
        jsonObject.Entries[1].Seats = premiumSeats;

        return apiService.put(apiUrl, jsonObject, { onError: handleError, onSuccess: handleSuccess }).catch();
    }

    forceSignout(tenantId: string): AxiosPromise {
        const apiUrl = `${this.apiBase}(${tenantId})/SetForceSignout`;

        return apiService.post(apiUrl)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }
}

export const tenantsService = new TenantsService();
